/*
 * <bundle-upsell>: quick-add for the Bundle & Save row in the cart drawer and
 * on the cart page. Adds 1 unit, then re-renders the cart with the same
 * section rendering flow that cart.js uses for quantity changes, so the line
 * items, discounts, totals and this upsell all refresh without a reload.
 */
if (!customElements.get('bundle-upsell')) {
  customElements.define(
    'bundle-upsell',
    class BundleUpsell extends HTMLElement {
      connectedCallback() {
        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      onClick(event) {
        const button = event.target.closest('.bundle-upsell__add');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        if (this.busy) return;
        this.add(button);
      }

      // The cart-items element that owns the re-render for this context.
      getCartItems() {
        if (this.dataset.context === 'drawer') return document.querySelector('cart-drawer-items');
        return document.querySelector('cart-items');
      }

      setBusy(button, busy) {
        this.busy = busy;
        this.classList.toggle('is-busy', busy);
        this.setAttribute('aria-busy', String(busy));
        this.querySelectorAll('.bundle-upsell__add').forEach((el) => {
          el.toggleAttribute('disabled', busy);
        });
        button.classList.toggle('is-loading', busy);
      }

      showError(message) {
        const error = this.querySelector('.bundle-upsell__error');
        if (!error) return;
        error.textContent = message;
        error.hidden = !message;
      }

      add(button) {
        const cartItems = this.getCartItems();
        const sections = cartItems && typeof cartItems.getSectionsToRender === 'function'
          ? cartItems.getSectionsToRender()
          : [];
        const productId = button.dataset.productId;
        const buttons = [...this.querySelectorAll('.bundle-upsell__add')];
        const position = buttons.indexOf(button);

        this.showError('');
        this.setBusy(button, true);

        const body = JSON.stringify({
          items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
          sections: sections.map((section) => section.section),
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then((state) => {
            if (state.status) {
              throw new Error(state.description || state.message || window.cartStrings.error);
            }
            if (!sections.length || !state.sections) {
              window.location.reload();
              return;
            }

            const context = this.dataset.context;
            sections.forEach((section) => {
              const host = document.getElementById(section.id);
              const html = state.sections[section.section];
              if (!host || !html) return;
              const target = host.querySelector(section.selector) || host;
              const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
              if (source) target.innerHTML = source.innerHTML;
            });

            const drawer = document.querySelector('cart-drawer');
            if (drawer) drawer.classList.remove('is-empty');
            const footer = document.getElementById('main-cart-footer');
            if (footer) footer.classList.remove('is-empty');

            // Cart is already re-rendered; tell other listeners (e.g. product
            // quantity rules) without making cart-items fetch it again.
            if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });

            this.restoreFocus(context, productId, position);
          })
          .catch((error) => {
            this.setBusy(button, false);
            this.showError(error.message || window.cartStrings.error);
            button.focus();
          });
      }

      // `this` is detached after a successful re-render; work off the fresh node.
      restoreFocus(context, productId, position) {
        const fresh = document.querySelector(`bundle-upsell[data-context="${context}"]`);
        const drawer = context === 'drawer' ? document.querySelector('cart-drawer') : null;
        let target = null;

        if (fresh) {
          const buttons = [...fresh.querySelectorAll('.bundle-upsell__add')];
          // The added product moves to the end of the row; keep the user at the
          // same slot so they can keep adding without hunting for the button.
          target =
            buttons.find((el, i) => i >= position && el.dataset.productId !== productId) ||
            buttons[0] ||
            fresh.querySelector('.bundle-upsell__message');
          const message = fresh.querySelector('.bundle-upsell__message');
          this.announce(context, message ? message.textContent.trim() : '');
        }
        if (!target) target = document.querySelector(drawer ? '.cart-drawer .cart-item__name' : '.cart-item__name');
        if (!target) return;

        if (drawer && typeof trapFocus === 'function') {
          trapFocus(drawer, target);
        } else {
          target.focus();
        }
        if (target.scrollIntoView) target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      announce(context, message) {
        const region = document.getElementById(
          context === 'drawer' ? 'CartDrawer-LiveRegionText' : 'cart-live-region-text'
        );
        if (!region || !message) return;
        region.setAttribute('aria-hidden', 'false');
        region.textContent = message;
      }
    }
  );
}
